import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../ui/alert-dialog';
import { Plus, Settings, Trash2, TestTube } from 'lucide-react';
import { formsApi } from '../../api/api';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { toast } from 'sonner';

interface SlackWebhookSetting {
  id: number;
  webhook_url: string;
  is_enabled: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

const SlackWebhookSettings: React.FC = () => {
  const { handleApiError } = useErrorHandler();
  const [webhooks, setWebhooks] = useState<SlackWebhookSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState<SlackWebhookSetting | null>(null);
  const [formData, setFormData] = useState({
    webhook_url: '',
    is_enabled: true,
  });
  const [testingWebhook, setTestingWebhook] = useState<number | null>(null);

  const fetchWebhooks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await formsApi.get('/slack-webhooks/');
      
      if (response.data.results) {
        setWebhooks(response.data.results);
      } else {
        setWebhooks(response.data);
      }
    } catch (error) {
      handleApiError(error as any, 'Failed to fetch Slack webhooks');
    } finally {
      setLoading(false);
    }
  }, [handleApiError]);

  useEffect(() => {
    fetchWebhooks();
  }, [fetchWebhooks]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingWebhook) {
        await formsApi.patch(`/slack-webhooks/${editingWebhook.id}/`, formData);
        toast.success('Slack webhook updated successfully');
      } else {
        await formsApi.post('/slack-webhooks/', formData);
        toast.success('Slack webhook created successfully');
      }
      
      setFormData({
        webhook_url: '',
        is_enabled: true,
      });
      setIsCreateDialogOpen(false);
      setEditingWebhook(null);
      fetchWebhooks();
    } catch (error) {
      handleApiError(error as any, editingWebhook ? 'Failed to update webhook' : 'Failed to create webhook');
    }
  };

  const handleEdit = (webhook: SlackWebhookSetting) => {
    setEditingWebhook(webhook);
    setFormData({
      webhook_url: webhook.webhook_url,
      is_enabled: webhook.is_enabled,
    });
    setIsCreateDialogOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await formsApi.delete(`/slack-webhooks/${id}/`);
      toast.success('Slack webhook deleted successfully');
      fetchWebhooks();
    } catch (error) {
      handleApiError(error as any, 'Failed to delete webhook');
    }
  };

  const handleTest = async (webhook: SlackWebhookSetting) => {
    try {
      setTestingWebhook(webhook.id);
      const response = await formsApi.post('/slack-webhooks/test/', {
        webhook_url: webhook.webhook_url,
      });
      
      if (response.data.success) {
        toast.success('Test message sent successfully!');
      } else {
        toast.error(response.data.message || 'Test failed');
      }
    } catch (error) {
      handleApiError(error as any, 'Failed to test webhook');
    } finally {
      setTestingWebhook(null);
    }
  };

  const resetForm = () => {
    setFormData({
      webhook_url: '',
      is_enabled: true,
    });
    setEditingWebhook(null);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium">Slack Webhooks</h3>
            <p className="text-sm text-muted-foreground">
              Configure Slack webhooks to receive notifications for new form submissions
            </p>
          </div>
        </div>
        <div className="flex justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Slack Webhooks</h3>
          <p className="text-sm text-muted-foreground">
            Configure Slack webhooks to receive notifications for new form submissions
          </p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={(open) => {
          setIsCreateDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Webhook
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingWebhook ? 'Edit Slack Webhook' : 'Add Slack Webhook'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="webhook_url">Webhook URL</Label>
                <Input
                  id="webhook_url"
                  type="url"
                  value={formData.webhook_url}
                  onChange={(e) => setFormData({ ...formData, webhook_url: e.target.value })}
                  placeholder="https://hooks.slack.com/services/..."
                  required
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_enabled"
                  checked={formData.is_enabled}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_enabled: checked as boolean })}
                />
                <Label htmlFor="is_enabled">Enable webhook</Label>
              </div>

              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingWebhook ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {webhooks.length === 0 ? (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
          <Settings className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No Slack Webhooks</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by creating your first webhook.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {webhooks.map((webhook) => (
            <div key={webhook.id} className="border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <div className={`h-3 w-3 rounded-full ${webhook.is_enabled ? 'bg-green-500' : 'bg-gray-400'}`} />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate" title={webhook.webhook_url}>
                        {webhook.webhook_url.length > 50 
                          ? `${webhook.webhook_url.substring(0, 50)}...` 
                          : webhook.webhook_url
                        }
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTest(webhook)}
                    disabled={testingWebhook === webhook.id}
                  >
                    <TestTube className="h-4 w-4" />
                    {testingWebhook === webhook.id ? 'Testing...' : 'Test'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(webhook)}
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Webhook</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete this webhook? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(webhook.id)}>
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SlackWebhookSettings;